import { ProjectsApi } from '@api';
import { Card, Span, Stack, Text, VStack } from '@chakra-ui/react';
import {
  FormController,
  FormInput,
  formSchemas,
  FormTextArea,
  RenderFormElement,
} from '@components/form';
import { useAppSelector } from '@state';
import { ProjectsType } from '@types';
import { OverlayDialog, toasterApi } from '@ui';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { z } from 'zod';

interface Props {
  defaultProjectSheet?: ProjectsType.ProjectSheet;
  isNew?: boolean;
}

const Inner_ManageCard: React.FC<Props> = ({
  defaultProjectSheet,
  isNew,
}: Props) => {
  const formSchema = isNew ? projectSheetSchema : projectSheetSchemaPartial;
  type FormData = z.infer<typeof formSchema>;

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );

  const [postProjectSheet, { isSuccess: isPostProjectSheetSuccess }] =
    ProjectsApi.Api.usePostProjectSheetMutation();
  const [patchProjectSheet, { isSuccess: isPatchProjectSheetSuccess }] =
    ProjectsApi.Api.usePatchProjectSheetMutation();

  const onFormSubmit = async (formData: FormData) => {
    OverlayDialog.open('confirmProjectSheetUpdate', {
      overlayId: 'confirmProjectSheetUpdate',
      title: `Confirm ${isNew ? 'Project Creation' : `Project Update`}`,
      description: `Are you sure you want to ${
        isNew ? 'create' : 'update'
      } the project?`,
      content: (
        <VStack align="flex-start" gap={2}>
          {!isNew && (
            <Text>
              Project :{' '}
              <Span
                textStyle={'heading.card'}
                color={'tertiary.fg'}
              >{`${defaultProjectSheet?.name}`}</Span>
            </Text>
          )}
          <RenderFormElement
            previousValue={
              isNew ? undefined : defaultProjectSheet?.name || undefined
            }
            currentValue={formData.name}
            label="Project Sheet Name"
          />
          <RenderFormElement
            previousValue={
              isNew ? undefined : defaultProjectSheet?.description || undefined
            }
            currentValue={formData.description}
            label="Project Sheet Description"
          />
        </VStack>
      ),
      handleConfirm: async () => {
        const projectData = {
          ...(formData.name && { name: formData.name }),
          ...(formData.description && {
            description: formData.description || '',
          }),
        };
        const { data, error } = isNew
          ? await postProjectSheet({
              name: formData.name ?? '',
              description: formData.description ?? '',
              project: selectedProject?.id ?? 0,
            })
          : await patchProjectSheet({
              projectSheetId: defaultProjectSheet!.id!,
              body: projectData,
            });
        if (data) {
          toasterApi({
            type: 'success',
            status: isNew ? 201 : 200,
            code: 'Success',
            message: `${isNew ? 'Created' : 'Updated'} Project`,
          });
        }
      },
    });
  };

  return (
    <>
      <Card.Root width="100%" variant="elevated">
        <Card.Body gap="7">
          <FormController
            key={`${defaultProjectSheet?.id}-${isNew}-form-${isPostProjectSheetSuccess}-${isPatchProjectSheetSuccess}`}
            label={
              isNew ? 'Create Project Sheet' : 'Update Project Sheet Details'
            }
            formSchema={formSchema}
            helperText={`Please fill out the form to ${
              isNew
                ? 'create a new project sheet'
                : 'update the project sheet details'
            }.`}
            onFormSubmit={onFormSubmit}
            defaultValues={{
              name: isNew ? undefined : defaultProjectSheet?.name || '',
              description: isNew
                ? undefined
                : defaultProjectSheet?.description || '',
            }}
            buttonProps={{
              variant: 'outline',
              color: 'tertiary.fg',
              width: '100%',
              mt: '4',
            }}
          >
            {({
              register,
              control,
              errors,
            }: {
              register: UseFormRegister<FormData>;
              control: Control<FormData>;
              errors: FieldErrors<FormData>;
            }) => {
              return (
                <>
                  <FormInput
                    control={control}
                    label="Project Sheet Name"
                    id="name"
                    register={register}
                    errors={errors}
                    helperText={formSchemas.projectSchema.nameHelper}
                  />
                  <FormTextArea
                    control={control}
                    label="Project Sheet Description"
                    id="description"
                    register={register}
                    errors={errors}
                    helperText={formSchemas.projectSchema.descriptionHelper}
                  />
                </>
              );
            }}
          </FormController>
        </Card.Body>
      </Card.Root>
    </>
  );
};

const ManageCard: React.FC<Props> = ({
  defaultProjectSheet,
  isNew = false,
}: Props) => {
  return (
    <Stack>
      <Inner_ManageCard
        defaultProjectSheet={defaultProjectSheet}
        isNew={isNew}
      />
    </Stack>
  );
};

export default ManageCard;

export const projectSheetSchema = z.object({
  name: formSchemas.projectSchema.nameSchema,
  description: z.string().optional(),
});

export const projectSheetSchemaPartial = projectSheetSchema.partial({
  name: true,
  description: true,
});
